import {
    ObservationOutcome,
    TestObservation,
} from '../types';

// FNP-861 — the reason this module is a triage surface and not a table dump.
//
// THE PROBLEM. A single wake produces rows that all look alike but mean very
// different things to whoever is triaging:
//
//   1 x FAIL     user.sign-in          <- the actual bug
//   3 x SKIPPED  (dependedOn: [...])   <- deferred; NOT independent failures
//   1 x SKIPPED  error ~ unimplemented <- a handler we never built; not a defect
//   1 x SKIPPED  error ~ no alias      <- a precondition that wasn't met
//
// Rendering five equal-looking red rows would actively mislead: it reads as five
// broken things when it is ONE broken thing plus four consequences. Worse, the
// three dependency-skips are the LOUDEST group by count, so a naive table makes
// the noise outrank the signal.
//
// THE RULE. `outcome` alone cannot separate these — three of the five share
// `outcome: SKIPPED`. Only the presence of `dependedOn` and the shape of the
// `error` string distinguish them, so classification is the seam that carries
// meaning into the UI. Everything visual (colour, icon, indentation, grouping)
// derives from this one function, so the table, the detail page and the
// synthetic-user panel can never disagree about what a row means.

export enum ObservationKind {
    // outcome: PASS and the platform recorded it. The healthy row.
    PASS = 'PASS',
    // outcome: PASS but verificationResult: NOT_VERIFIED. The click worked and the
    // platform did NOT record it — the highest-value signal the fleet produces, and
    // the one that a naive "green means fine" table would hide completely.
    PASS_UNVERIFIED = 'PASS_UNVERIFIED',
    // outcome: FAIL. A genuine defect, and the root of any cascade around it.
    FAIL = 'FAIL',
    // outcome: SKIPPED with dependedOn — deferred because something upstream failed.
    // Not a defect in its own right. Never colour these the same as a FAIL.
    DEPENDENCY_SKIP = 'DEPENDENCY_SKIP',
    // outcome: SKIPPED because no handler is implemented for the actionCode. This is
    // BUILD STATUS, not breakage — an unregistered actionCode is legal by design
    // (the executor fails open). Must not read as red.
    UNIMPLEMENTED = 'UNIMPLEMENTED',
    // outcome: SKIPPED for any other stated reason (e.g. "no alias available") — a
    // precondition that wasn't met. Informational.
    PRECONDITION_SKIP = 'PRECONDITION_SKIP',
}

// The executor writes the unimplemented marker into the free-text `error` string;
// there is no dedicated field for it. Matching is substring + case-insensitive so a
// wording change ("unimplemented action", "handler unimplemented") still classifies.
// If the executor ever gains a structured reason code, switch to it and delete this.
const UNIMPLEMENTED_MARKER = 'unimplemented';

export const hasDependencies = (record: Pick<TestObservation, 'dependedOn'>): boolean =>
    Array.isArray(record.dependedOn) && record.dependedOn.filter(Boolean).length > 0;

// The upstream actionCodes this row deferred to, with nulls stripped — `dependedOn`
// is [String] (nullable members) on the schema.
export const dependencyCodes = (record: Pick<TestObservation, 'dependedOn'>): string[] =>
    (record.dependedOn ?? []).filter((code): code is string => typeof code === 'string' && code !== '');

export const classifyObservation = (
    record: Pick<TestObservation, 'outcome' | 'error' | 'verificationResult' | 'dependedOn'>,
): ObservationKind => {

    if (record.outcome === ObservationOutcome.FAIL) {
        return ObservationKind.FAIL;
    }

    if (record.outcome === ObservationOutcome.PASS) {
        // verificationResult gets equal billing with outcome: a PASS the platform
        // never recorded is a finding, not a success.
        return record.verificationResult === 'NOT_VERIFIED'
            ? ObservationKind.PASS_UNVERIFIED
            : ObservationKind.PASS;
    }

    // SKIPPED — the three-way split that `outcome` cannot express.
    // dependedOn is checked FIRST: it is a structured field, whereas the
    // unimplemented marker is a substring match on free text. A row that carries
    // both is a deferral (the dependency is why it did not run at all).
    if (hasDependencies(record)) {
        return ObservationKind.DEPENDENCY_SKIP;
    }

    if (typeof record.error === 'string' && record.error.toLowerCase()
                                                  .includes(UNIMPLEMENTED_MARKER)) {
        return ObservationKind.UNIMPLEMENTED;
    }

    return ObservationKind.PRECONDITION_SKIP;
};

// Presentation contract for a kind. Held here (not in the badge component) so the
// table row, the badge and the detail page all read the same source.
//
// `severity` drives ordering — it is what puts the root cause above its own
// consequences regardless of timestamp. `muted` marks the rows that must visually
// recede so the cascade's dependents cannot outshout the bug that caused them.
export type ObservationKindDisplay = {
    label: string,
    color: string,
    severity: number,
    muted: boolean,
    description: string,
};

export const OBSERVATION_KIND_DISPLAY: Record<ObservationKind, ObservationKindDisplay> = {
    [ObservationKind.FAIL]: {
        label: 'Failed',
        color: 'red',
        severity: 0,
        muted: false,
        description: 'The action failed. This is a real defect.',
    },
    [ObservationKind.PASS_UNVERIFIED]: {
        label: 'Passed, not recorded',
        color: 'orange',
        severity: 1,
        muted: false,
        description: 'The action succeeded but the platform did not record it — the interaction worked and the resulting platform state did not follow.',
    },
    [ObservationKind.DEPENDENCY_SKIP]: {
        label: 'Skipped — blocked',
        color: 'gray',
        severity: 2,
        muted: true,
        description: 'Did not run because an action it depends on failed. Fix the root cause, not this row.',
    },
    [ObservationKind.PRECONDITION_SKIP]: {
        label: 'Skipped — precondition',
        color: 'gray',
        severity: 3,
        muted: true,
        description: 'Did not run because a precondition was not met.',
    },
    [ObservationKind.UNIMPLEMENTED]: {
        label: 'Not implemented',
        color: 'blue',
        severity: 4,
        muted: true,
        description: 'No handler is built for this action code yet. This is build status, not breakage.',
    },
    [ObservationKind.PASS]: {
        label: 'Passed',
        color: 'green',
        severity: 5,
        muted: false,
        description: 'The action succeeded and the platform recorded it.',
    },
};

export const observationKindDisplay = (kind: ObservationKind): ObservationKindDisplay =>
    OBSERVATION_KIND_DISPLAY[kind];
